function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/* eslint-disable max-statements */
import React from 'react';
import classNames from 'classnames';
import { AppError, EipError } from 'src/redux/types';
import { useTypedDispatch, useTypedSelector } from 'src/redux/utils';
import { invalidateRentItem, prepareRentItem, rentItem } from 'src/redux/actions';
import { updateWalletBalance } from '@profile/src/redux/remoteActions';
import ErrorMessage from './ErrorMessage';
import styles from './styles.module.scss';
import icClose from 'assets/svg/ic_close.svg';
import icThetanCoin from 'assets/img/thetan_coin.png';
import icQuestionMark from 'assets/svg/ic_help_v2.svg';
import { formatCurrency } from '@wolffun/system-currency';
import Modal from 'src/Components/Modal';
import { BN } from 'bn.js';
import { formatErrorMessage } from './utils';
import { checkItemLocked } from 'src/utils/ethereum';
import { formatPeriodDay } from '../../RentOutProvider/RentOutHeroModal/utils';
import { ReactTooltip, TooltipPosition } from 'src/Components/TooltipProviderV2';
import Button from '../../Components/Button';
import Checkbox from 'src/Components/CheckboxV2';
import { getPreferredWalletProviderName } from 'src/utils/session';
import useCheckAllowance from 'src/hooks/useCheckAllowance';
import { HERO_RENTAL_ADDRESS } from 'src/constants';
import { useConfirmOpenWalletConnect } from 'src/Components/WalletConnectConfirmProvider';
import { useTestId } from '@wolffun/testable-id';
const DEFAULT_WIN_RATE = 0.65;

const TransactionConfirmModal = ({
  hero,
  onClose,
  onRentCompleted
}) => {
  const {
    generateTestIdProps
  } = useTestId();
  const dispatch = useTypedDispatch();
  const {
    confirmOpenWalletConnect
  } = useConfirmOpenWalletConnect();
  const rentInfo = useTypedSelector(state => {
    var _state$itemDetail$her;

    return (_state$itemDetail$her = state.itemDetail.heroDetail) === null || _state$itemDetail$her === void 0 ? void 0 : _state$itemDetail$her.hero.rentInfo;
  });
  const price = useTypedSelector(state => {
    var _state$itemDetail$her2;

    return (_state$itemDetail$her2 = state.itemDetail.heroDetail) === null || _state$itemDetail$her2 === void 0 ? void 0 : _state$itemDetail$her2.hero.rentInfo.cost;
  });
  const rentBattles = useTypedSelector(state => {
    var _state$itemDetail$her3;

    return ((_state$itemDetail$her3 = state.itemDetail.heroDetail) === null || _state$itemDetail$her3 === void 0 ? void 0 : _state$itemDetail$her3.hero.rentInfo.rentBattles) || 0;
  });
  const thcBalance = useTypedSelector(state => state.connectWallet.thcBalance);
  const rentLoading = useTypedSelector(state => state.rentItem.loading);
  const [showWaiting, setShowWaiting] = React.useState(false);
  const [isUserAccept, setIsUserAccept] = React.useState(false);
  const [error, setError] = React.useState(AppError.NO_ERROR);
  const {
    approveUseBalance,
    isEnoughAllowance,
    isProcessing
  } = useCheckAllowance({
    contractAddress: HERO_RENTAL_ADDRESS,
    thresholdPrice: price
  });
  const errorMessage = React.useMemo(() => {
    return formatErrorMessage(error);
  }, [error]);
  React.useEffect(() => {
    const checkLocked = async () => {
      const {
        isLocked
      } = await checkItemLocked(hero.hero.tokenId);

      if (isLocked) {
        setError(AppError.HERO_HAS_BEEN_RENTED);
      }
    };

    checkLocked();
  }, [hero.hero.tokenId]);

  const renderTooltip = () => /*#__PURE__*/React.createElement("div", {
    className: styles.tooltipContainer
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.tooltip
  }, "Predicted THC earned by Renter with win rate of\xA0", DEFAULT_WIN_RATE * 100, "%:", /*#__PURE__*/React.createElement("span", {
    className: styles.currencyValue
  }, "\xA0", rentInfo === null || rentInfo === void 0 ? void 0 : rentInfo.winRateTHC[DEFAULT_WIN_RATE * 100], " THC")));

  const onRentButtonPressed = async () => {
    if (price) {
      const preparedData = await dispatch(prepareRentItem({
        renterAddress: hero.hero.userAddress,
        systemPrice: price,
        tokenId: Number(hero.hero.tokenId)
      }));
      confirmOpenWalletConnect(async () => {
        const {
          error: rentError,
          data
        } = await dispatch(rentItem(preparedData));

        if (rentError !== undefined && rentError !== AppError.NO_ERROR) {
          setError(rentError);
          return;
        }

        if (data !== null && data !== void 0 && data.id && thcBalance) {
          onRentCompleted(hero, thcBalance);
        }
      });
    }
  };

  React.useEffect(() => {
    const thcBalanceBN = thcBalance ? new BN(thcBalance.value) : undefined;
    const priceBN = price ? new BN(price.value) : undefined;

    if (priceBN && thcBalanceBN && thcBalanceBN.lt(priceBN)) {
      setError(AppError.INSUFFICIENT_BALANCE);
    }
  }, [thcBalance, price]);
  React.useEffect(() => {
    if (rentLoading) {
      const timeout = setTimeout(() => setShowWaiting(true), 10 * 1000);
      return () => clearTimeout(timeout);
    } else {
      setShowWaiting(false);
      return undefined;
    }
  }, [rentLoading]);
  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = () => {
    dispatch(updateWalletBalance());
    dispatch(invalidateRentItem());
  };

  return /*#__PURE__*/React.createElement(Modal, null, /*#__PURE__*/React.createElement("div", {
    className: styles.modal
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Rent hero"), /*#__PURE__*/React.createElement("button", {
    className: styles.close,
    disabled: rentLoading,
    onClick: onClose
  }, /*#__PURE__*/React.createElement("img", {
    src: icClose
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.detailContainer
  }, /*#__PURE__*/React.createElement("div", {
    className: styles.subtitle
  }, `You are about to rent #${hero.hero.tokenId} ${hero.skin.name} ${hero.heroType.name}`), /*#__PURE__*/React.createElement("div", {
    className: styles.detailSection
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles.row, styles.battlesForRent)
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Battles for rent"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('battles-for-rent')), rentBattles)), /*#__PURE__*/React.createElement("div", {
    className: styles.row
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Price"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('hero-price')), price && formatCurrency(price, {
    withCurrency: false
  }), "\xA0", /*#__PURE__*/React.createElement("img", {
    className: styles.currencyIcon,
    src: icThetanCoin
  }), "THC"), /*#__PURE__*/React.createElement(ReactTooltip, {
    className: styles.tooltip,
    position: TooltipPosition.TOP_LEFT,
    content: renderTooltip()
  }, /*#__PURE__*/React.createElement("img", {
    className: styles.questionIcon,
    src: icQuestionMark
  })))), /*#__PURE__*/React.createElement("div", {
    className: styles.priceBreakdownRow
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.label
  }, "Your balance"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.price
  }, generateTestIdProps('user-balance')), thcBalance && formatCurrency(thcBalance, {
    withCurrency: true
  }))), /*#__PURE__*/React.createElement("div", {
    className: styles.confirmSection
  }, /*#__PURE__*/React.createElement(Checkbox, _extends({
    check: isUserAccept,
    onToggle: value => setIsUserAccept(value),
    className: styles.checkbox
  }, generateTestIdProps('understand'))), /*#__PURE__*/React.createElement("span", {
    className: styles.content
  }, "I understand that this rental can't be canceled during its active period")), errorMessage ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    title: errorMessage.title,
    description: errorMessage.description
  }) : null, showWaiting ? /*#__PURE__*/React.createElement(ErrorMessage, {
    className: styles.error,
    warning: true,
    title: "It may take a while...",
    description: `We are still waiting for transaction to be processed. Please be patient. You might need to check your ${getPreferredWalletProviderName() || ''} wallet.`
  }) : null, rentInfo !== null && rentInfo !== void 0 && rentInfo.periodHours ? /*#__PURE__*/React.createElement("div", {
    className: styles.wrapFooter
  }, /*#__PURE__*/React.createElement("span", {
    className: styles.periodLabel
  }, "Renter will keep the hero for maximum"), /*#__PURE__*/React.createElement("span", _extends({
    className: styles.periodValue
  }, generateTestIdProps('rental-period')), formatPeriodDay(rentInfo.periodHours))) : undefined, /*#__PURE__*/React.createElement("div", {
    className: styles.buttonGroup
  }, rentLoading || isProcessing ? /*#__PURE__*/React.createElement(Button, {
    title: "Processing",
    disabled: true,
    loading: true
  }) : isEnoughAllowance ? /*#__PURE__*/React.createElement(Button, {
    title: "Rent",
    onClick: onRentButtonPressed,
    disabled: !isUserAccept || error !== AppError.NO_ERROR
  }) : /*#__PURE__*/React.createElement(Button, {
    title: "Approve",
    onClick: async () => {
      const {
        error
      } = await approveUseBalance();

      if (error !== EipError.NO_ERROR) {
        setError(error);
      }
    },
    disabled: !isUserAccept || error !== AppError.NO_ERROR
  })))));
};

export default TransactionConfirmModal;